import type { ReactNode } from "react";

/**
 * Shared form primitives used by Onboarding + every Quick-log Drawer.
 * SPEC 3.1.4 "Quick-log dialog shapes": uniform layout across all 9 dialogs.
 */

export function Row({ children }: { children: ReactNode }) {
  // Each grid cell carries `min-width: 0` so its `<input>` (whose intrinsic
  // size is ~150px) can shrink below its content width and the row stays
  // inside the dialog. Without this, a 3-up Row of number inputs overflows
  // the centered modal at every breakpoint (BW17 Issue 5).
  const count = Array.isArray(children) ? children.length : 1;
  return (
    <div
      className="haus-form-row"
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))`,
        gap: "var(--space-3)",
      }}
    >
      <style>{`
        .haus-form-row > * { min-width: 0; }
        @media (max-width: 480px) {
          .haus-form-row { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
        }
      `}</style>
      {children}
    </div>
  );
}

export function FormInput({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  required,
  autoFocus,
  inputMode,
  maxLength,
  step,
  min,
  max,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
  autoFocus?: boolean;
  inputMode?: "text" | "decimal" | "numeric" | "email" | "tel" | "url" | "search";
  maxLength?: number;
  step?: string;
  min?: string | number;
  max?: string | number;
}) {
  return (
    <label
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-2)",
        fontSize: "var(--type-body-s)",
        color: "var(--fg-2)",
        minWidth: 0,
      }}
    >
      {label}
      <input
        type={type}
        value={value}
        required={required}
        autoFocus={autoFocus}
        inputMode={inputMode}
        maxLength={maxLength}
        step={step}
        min={min}
        max={max}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={{
          height: 38,
          width: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          padding: "0 var(--space-3)",
          background: "var(--bg-3)",
          color: "var(--fg)",
          border: "1px solid var(--hair-2)",
          borderRadius: "var(--radius-card)",
          fontFamily: "var(--font-sans)",
          fontSize: "var(--type-body)",
        }}
      />
    </label>
  );
}

export function FormSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-2)",
        fontSize: "var(--type-body-s)",
        color: "var(--fg-2)",
        minWidth: 0,
      }}
    >
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          height: 38,
          width: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          padding: "0 var(--space-3)",
          background: "var(--bg-3)",
          color: "var(--fg)",
          border: "1px solid var(--hair-2)",
          borderRadius: "var(--radius-card)",
          fontFamily: "var(--font-sans)",
          fontSize: "var(--type-body)",
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function FormTextarea({
  label,
  value,
  onChange,
  rows = 3,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  placeholder?: string;
}) {
  return (
    <label
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-2)",
        fontSize: "var(--type-body-s)",
        color: "var(--fg-2)",
        minWidth: 0,
      }}
    >
      {label}
      <textarea
        value={value}
        rows={rows}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={{
          padding: "var(--space-3)",
          width: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          background: "var(--bg-3)",
          color: "var(--fg)",
          border: "1px solid var(--hair-2)",
          borderRadius: "var(--radius-card)",
          fontFamily: "var(--font-sans)",
          fontSize: "var(--type-body)",
          resize: "vertical",
        }}
      />
    </label>
  );
}

export function PrimaryButton({
  children,
  type = "button",
  onClick,
  disabled,
}: {
  children: ReactNode;
  type?: "button" | "submit";
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        height: 38,
        padding: "0 var(--space-5)",
        background: "var(--accent)",
        color: "#0a0b0d",
        fontWeight: 600,
        fontSize: "var(--type-body-s)",
        borderRadius: "var(--radius-card)",
        border: "1px solid transparent",
        cursor: disabled ? "wait" : "pointer",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {children}
    </button>
  );
}

export function SecondaryButton({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        height: 38,
        padding: "0 var(--space-4)",
        background: "transparent",
        color: "var(--fg-2)",
        fontSize: "var(--type-body-s)",
        borderRadius: "var(--radius-card)",
        border: "1px solid var(--hair-2)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}

export function DialogFooter({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-end",
        gap: "var(--space-2)",
        marginTop: "var(--space-2)",
      }}
    >
      {children}
    </div>
  );
}

/** Local-now in `YYYY-MM-DDTHH:MM` format suitable for `<input type="datetime-local">` */
export function localNowDatetime(): string {
  const d = new Date();
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

export function browserTz(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}
